//================================================================
//  Component: Add User Pane ~ Child component of 'AdminUserTable.js'
//================================================================

//  Purpose: Add user to roles document

//  Properties:
//    - documentid = {string, documentid e.g. agreements, applications, etc }
//    - openDeletePane = {useState, contains the user object}
//    - setOpenDeleteModal = {useState used to determine if the side pane is open/closed}

//  Example:
//    <AdminUserTableAddUser
//      documentid={'agreements'}
//      openAddModal={openAddModal}
//      setOpenAddModal={setOpenAddModal}
//    ></AdminUserTableAddUser>    

//================================================================

//Libraries
import React, { useContext, useState, useReducer } from 'react';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../../../../../Library/GlobalContexts';

//Components
import UserSearch from '../../../../../../Components/UserSearch/UserSearch';

//Functions
import WriteDocument from '../../../../../../Library/WriteDocument';

//Images
import Info from '../../../../../../Components/Images/Icon_Info_Blue.svg';
import LoadingIcon from '../../../../../../Components/Images/Image_Loading_Ripple.svg';
import Error from '../../../../../../Components/Images/Icon_Warning_Red.svg';


export default function AdminUserTableAddUser({
    documentid,
    openAddModal,
    setOpenAddModal,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'onload', 'pending', 'error'
    const [modalStatus, setModalStatus] = useState('onload');

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'newUserInput': '', // email of the selected user
            'newUserName': '', // fullname of the selected user
            'newGivenname': '', // givenname of the selected user
            'newSurname': '', // surname of the selected user
            'newJobTitle': '', // jobtitle of the selected user
            'searchResults': '',
            'queryMessage': '',

            'errorMessage': '',
            'preventSubnet': true,
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handle validation of selected user, via a CB function from SearchUser
    function onChangeHandler(searchDataObject) {

        if (searchDataObject === undefined) return;
        if (openAddModal === undefined) return;

        // Validate that the user ISN'T already an admin
        const userCheck = openAddModal.users.filter((object) => object.emailaddress === searchDataObject.newUserInput).length;
        if (userCheck > 0) {

            setSearchData({
                'errorMessage': 'User is already added. Update the existing permission.',
                'preventSubnet': true,
            });

            return;

        }

        // Allow the submission
        setSearchData({
            'errorMessage': '',
            'preventSubnet': false,
        });

    }

    // Submits changes to the table
    function addUser(emailaddress) {

        if (emailaddress === undefined) return;
        if (getRoles === undefined) return;

        // Remove user from Firestore map
        const users = getRoles[documentid];

        // Admin 
        if (openAddModal.type === 'admin') {

            users[emailaddress] = {
                'admin': true,
                'delete': true,
                'write': true,
                'create': true,
                'download': true,
                'read': true,
                'jobtitle': searchData.newJobTitle,
                'givenname': searchData.newGivenname,
                'surname': searchData.newSurname,
            };

            // User 
        } else if (openAddModal.type === 'user') {

            users[emailaddress] = {
                'admin': false,
                'delete': false,
                'write': false,
                'create': false,
                'download': false,
                'read': true,
                'jobtitle': searchData.newJobTitle,
                'givenname': searchData.newGivenname,
                'surname': searchData.newSurname,
            };

        } else {

            console.log('AdminUserTableAddUser - Failed to complete, the selected user "type" does NOT exist.',);
            setModalStatus('error');

        }

        //----------------------------
        //  Update Roles Doc
        //----------------------------

        // Write over the WHOLE document!
        const document = {
            'collectionid': documentid,
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'users': users
        };

        WriteDocument('roles', documentid, document, false).then(() => {

            //----------------------------
            //  Send email notification
            //----------------------------

            const supportDoc = {
                'caseid': `inc-${Date.now()}`,
                'createdby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'createddate': new Date(),
                'requestedfor': {
                    'email': emailaddress,
                    'givenname': searchData.newGivenname,
                    'surname': searchData.newSurname,
                },
                'portal': 'Inventory',
                'casetype': 'Permission Provided',
                'description': '', // String
                'link': process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ?
                    `https://devinventory.digitalservices.lendlease.com/inventory?view=${documentid}` // dev

                    : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ?
                        `https://uatinventory.digitalservices.lendlease.com/inventory?view=${documentid}` // uat

                        :
                        `https://inventory.digitalservices.lendlease.com/inventory?view=${documentid}`, // prod
                'permissionlevel': '',
            };

            return WriteDocument('support', supportDoc.caseid, supportDoc, false).then(() => {

                // Set success toast
                setToast({
                    'type': 'success',
                    'message': `${openAddModal.type === 'admin' ? 'Admin Access' : 'User Access'} successfully granted.`,
                });

                setModalStatus('onload');
                setSearchData({
                    'newUserInput': '', // email of the selected user
                    'newUserName': '', // fullname of the selected user
                    'newGivenname': '', // givenname of the selected user
                    'newSurname': '', // surname of the selected user
                    'newJobTitle': '', // jobtitle of the selected user
                    'searchResults': '',
                    'queryMessage': '',

                    'errorMessage': '',
                    'preventSubnet': true,
                });
                setOpenAddModal({
                    'type': '',
                    'users': []
                });

            });

        }).catch((error) => {

            console.log('error', error);
            setModalStatus('error');

        });

    };

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (openAddModal?.type !== 'admin' && openAddModal?.type !== 'user') return null;

    //====================================================
    //  Pending
    //====================================================

    if (modalStatus === 'pending') {

        return (
            <div className='Modal-Background'>
                <dialog className='Warning-Modal-Container'>

                    <div className='flex flex-col justify-center align-middle'>

                        <img className='w-[130px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                        <button className='Primary-Button w-fit self-center' onClick={() => {

                            setModalStatus('onload');
                            setSearchData({
                                'newUserInput': '', // email of the selected user
                                'newUserName': '', // fullname of the selected user
                                'newGivenname': '', // givenname of the selected user
                                'newSurname': '', // surname of the selected user
                                'newJobTitle': '', // jobtitle of the selected user
                                'searchResults': '',
                                'queryMessage': '',

                                'errorMessage': '',
                                'preventSubnet': true,
                            });
                            setOpenAddModal({
                                'type': '',
                                'users': []
                            });

                        }}>
                            Cancel
                        </button>

                    </div>

                </dialog>

            </div>
        )

    }

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (modalStatus === 'onload') {

        return (
            <div className='Pane-Background'>

                {/* Modal Container */}
                <dialog className='Pane-Container'>

                    <div className='grid grid-rows-[70px_1fr_80px] w-full overflow-hidden'>

                        {/* ------------------------------- */}
                        {/*  Header                         */}
                        {/* ------------------------------- */}

                        <div className='flex flex-col gap-2 p-4 border-b border-solid border-[#D8D8D8] pb-2'>
                            <h4 className='text-[22px] p-0 m-0'> Add {openAddModal.type === 'admin' ? 'Admin' : 'User'}</h4>
                        </div>

                        {/* ------------------------------- */}
                        {/*  Body                           */}
                        {/* ------------------------------- */}

                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* Disclaimer */}
                            <div className='flex flex-row gap-3 px-[15px] pt-[12px] text-[15px] rounded-md mx-1 my-3 bg-[#E9F7FF] border-1 border-[#0972D3]'>
                                <div>
                                    <img src={Info} alt='info'></img>
                                </div>

                                {/* Admin vs User disclaimer */}
                                {
                                    openAddModal.type === 'admin' ?
                                        <ul className='list-disc'>
                                            <li>The user will be granted <strong>full admin access</strong> to the table immediately upon being added.</li>
                                            <li>This includes permissions to <strong>read</strong>, <strong>download</strong>, <strong>create</strong>, <strong>modify</strong>, <strong>delete</strong>, and <strong>add or manage</strong> other users.</li>
                                            <li>An <strong>email notification</strong> will be sent to the user confirming their access.</li>
                                            <li>You can modify their permission settings at any time after they've been added.</li>
                                        </ul>
                                        :
                                        <ul className='list-disc'>
                                            <li>The user will be granted <strong>read-only access</strong> to the table immediately upon being added.</li>
                                            <li>An <strong>email notification</strong> will be sent to the user confirming their access.</li>
                                            <li>You can modify their permission settings at any time after they've been added.</li>
                                        </ul>
                                }

                            </div>

                            {/* Type in user you would like to add */}
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold'>Add by email:</label>
                                <UserSearch
                                    selectedUser={searchData}
                                    setSelectedUser={setSearchData}
                                    errorMessage={searchData.errorMessage}
                                    onChangeHandler={onChangeHandler}
                                ></UserSearch>
                            </div>

                        </div>

                        {/* ------------------------------- */}
                        {/*  Submit Buttons                 */}
                        {/* ------------------------------- */}

                        <div className='flex flex-row gap-2 px-4 py-3'>

                            <button disabled={searchData.newUserName.length === 0 || searchData.preventSubnet === true} className='Primary-Button' onClick={() => addUser(searchData.newUserInput)}>Add</button>
                            <button
                                className='Secondary-Button'
                                onClick={() => {

                                    setModalStatus('onload');
                                    setSearchData({
                                        'newUserInput': '', // email of the selected user
                                        'newUserName': '', // fullname of the selected user
                                        'newGivenname': '', // givenname of the selected user
                                        'newSurname': '', // surname of the selected user
                                        'newJobTitle': '', // jobtitle of the selected user
                                        'searchResults': '',
                                        'queryMessage': '',

                                        'errorMessage': '',
                                        'preventSubnet': true,
                                    });
                                    setOpenAddModal({
                                        'type': '',
                                        'users': []
                                    });

                                }}
                            >
                                Cancel
                            </button>

                        </div>

                    </div>

                </dialog>

            </div>
        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else {

        return (
            <div className='Modal-Background'>

                {/* Modal Container */}
                <dialog className='Warning-Modal-Container justify-center items-center w-[600px]'>

                    <img className='my-1 w-[50px]' src={Error} alt='Error'></img>

                    <h4 className='mb-0'>Oops! Something went wrong.</h4>

                    <p className='text-center leading-[1.7]'>
                        An error occurred while we processed your request.
                    </p>

                    <button
                        className='Warning-Button'
                        onClick={() => {

                            setModalStatus('onload');
                            setSearchData({
                                'newUserInput': '', // email of the selected user
                                'newUserName': '', // fullname of the selected user
                                'newGivenname': '', // givenname of the selected user
                                'newSurname': '', // surname of the selected user
                                'newJobTitle': '', // jobtitle of the selected user
                                'searchResults': '',
                                'queryMessage': '',

                                'errorMessage': '',
                                'preventSubnet': true,
                            });
                            setOpenAddModal({
                                'type': '',
                                'users': []
                            });

                        }}
                    >
                        Close
                    </button>

                </dialog>

            </div>
        )

    }

    //------------------------------------------------------

}
